import React from "react";

// Feedback for an AI-marked open-ended answer.
//
// Three states: being marked, marked, or couldn't be marked. The verdict is the score
// plus feedback on THIS student's answer — never the mark scheme, which would hand over
// the model answer and duplicate the explanation the student can already open.
//
// Two themes, because this appears on both surfaces: `light` inside the white quiz card,
// `dark` on the navy results page. Colour is carried on translucent overlays in the dark
// theme so the panel sits in the page rather than looking like a pasted-in light card.

const TONE = {
  light: {
    correct: {
      label: "Full marks",
      shell: "border-emerald-200 bg-emerald-50/70",
      chip: "bg-emerald-600 text-white",
      bar: "bg-emerald-500",
      track: "bg-white/70",
      heading: "text-emerald-900",
      body: "text-emerald-900/80",
      stamp: "text-slate-400",
    },
    partial: {
      label: "Partly correct",
      shell: "border-amber-200 bg-amber-50/70",
      chip: "bg-amber-500 text-white",
      bar: "bg-amber-500",
      track: "bg-white/70",
      heading: "text-amber-900",
      body: "text-amber-900/80",
      stamp: "text-slate-400",
    },
    incorrect: {
      label: "Not yet",
      shell: "border-rose-200 bg-rose-50/70",
      chip: "bg-rose-500 text-white",
      bar: "bg-rose-400",
      track: "bg-white/70",
      heading: "text-rose-900",
      body: "text-rose-900/80",
      stamp: "text-slate-400",
    },
  },
  dark: {
    correct: {
      label: "Full marks",
      shell: "border-emerald-400/25 bg-emerald-400/10",
      chip: "bg-emerald-500 text-white",
      bar: "bg-emerald-400",
      track: "bg-white/10",
      heading: "text-emerald-300",
      body: "text-white/70",
      stamp: "text-white/30",
    },
    partial: {
      label: "Partly correct",
      shell: "border-amber-400/25 bg-amber-400/10",
      chip: "bg-amber-500 text-white",
      bar: "bg-amber-400",
      track: "bg-white/10",
      heading: "text-amber-300",
      body: "text-white/70",
      stamp: "text-white/30",
    },
    incorrect: {
      label: "Not yet",
      shell: "border-rose-400/25 bg-rose-500/10",
      chip: "bg-rose-500 text-white",
      bar: "bg-rose-400",
      track: "bg-white/10",
      heading: "text-rose-300",
      body: "text-white/70",
      stamp: "text-white/30",
    },
  },
};

const SURFACE = {
  light: {
    panel: "border-slate-200 bg-white/80",
    title: "text-slate-700",
    sub: "text-slate-500",
    track: "bg-slate-100",
    failShell: "border-slate-300 bg-slate-50",
    failTitle: "text-slate-800",
    failBody: "text-slate-600",
    failButton: "border-slate-300 bg-white text-slate-700 hover:bg-slate-100",
  },
  dark: {
    panel: "border-white/10 bg-white/5",
    title: "text-white/90",
    sub: "text-white/50",
    track: "bg-white/10",
    failShell: "border-white/15 bg-white/5",
    failTitle: "text-white/90",
    failBody: "text-white/60",
    failButton: "border-white/20 bg-white/10 text-white hover:bg-white/20",
  },
};

// ── Being marked ─────────────────────────────────────────────────────────────
const GradingInProgress = ({ surface }) => (
  <div className={`rounded-xl border px-4 py-3.5 ${surface.panel}`}>
    <div className="flex items-center gap-2.5">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-sky-500" />
      </span>
      <p className={`text-sm font-medium ${surface.title}`}>Marking your answer</p>
    </div>

    <p className={`mt-1 ml-[18px] text-xs ${surface.sub}`}>
      Hang tight — this only takes a few seconds.
    </p>

    <div className={`mt-3 h-1 w-full overflow-hidden rounded-full ${surface.track}`}>
      <div className="h-full w-1/3 animate-[shimmer_1.4s_ease-in-out_infinite] rounded-full bg-gradient-to-r from-sky-300 via-sky-500 to-sky-300" />
    </div>

    <style>{`
      @keyframes shimmer {
        0%   { transform: translateX(-100%); }
        100% { transform: translateX(320%); }
      }
    `}</style>
  </div>
);

// ── Couldn't be marked ───────────────────────────────────────────────────────
const GradingFailed = ({ surface, message, retryable, onRetry }) => (
  <div className={`rounded-xl border px-4 py-3.5 ${surface.failShell}`}>
    <p className={`text-sm font-medium ${surface.failTitle}`}>Couldn't mark this answer</p>
    <p className={`mt-1 text-xs leading-relaxed ${surface.failBody}`}>{message}</p>
    {retryable && onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className={`mt-2.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${surface.failButton}`}
      >
        Try again
      </button>
    )}
  </div>
);

// ── Marked ───────────────────────────────────────────────────────────────────
// The mark scheme itself is deliberately not shown. The grader is instructed never to
// reproduce it, and listing every creditable point here would both undo that and hand
// over the model answer — which is the explanation's job, on the student's own terms.
// The criteria are still recorded in ai_grading_breakdown for review.
const GradedVerdict = ({ theme, outcome, marksAwarded, maxMarks, feedback }) => {
  const tone = TONE[theme][outcome] ?? TONE[theme].incorrect;
  const pct = maxMarks > 0 ? Math.round((marksAwarded / maxMarks) * 100) : 0;

  return (
    <div className={`rounded-xl border px-4 py-3.5 ${tone.shell}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold tabular-nums ${tone.chip}`}
          >
            {marksAwarded} / {maxMarks}
          </span>
          <span className={`text-sm font-semibold ${tone.heading}`}>{tone.label}</span>
        </div>
        <span className={`text-[11px] font-medium uppercase tracking-wider ${tone.stamp}`}>
          Marked
        </span>
      </div>

      {/* Marks earned, at a glance */}
      <div className={`mt-2.5 h-1.5 w-full overflow-hidden rounded-full ${tone.track}`}>
        <div
          className={`h-full rounded-full transition-all duration-700 ease-out ${tone.bar}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {feedback && (
        <p className={`mt-3 text-xs leading-relaxed ${tone.body}`}>{feedback}</p>
      )}
    </div>
  );
};

const ProbabilisticResponseFeedback = ({
  state,            // "grading" | "graded" | "failed"
  theme = "light",  // "light" on the white quiz card, "dark" on the navy results page
  outcome,
  marksAwarded,
  maxMarks,
  feedback,
  message,
  retryable,
  onRetry,
}) => {
  const surface = SURFACE[theme] ?? SURFACE.light;

  if (state === "grading") return <GradingInProgress surface={surface} />;
  if (state === "failed")
    return (
      <GradingFailed
        surface={surface}
        message={message}
        retryable={retryable}
        onRetry={onRetry}
      />
    );
  if (state === "graded")
    return (
      <GradedVerdict
        theme={theme in TONE ? theme : "light"}
        outcome={outcome}
        marksAwarded={marksAwarded}
        maxMarks={maxMarks}
        feedback={feedback}
      />
    );
  return null;
};

export default ProbabilisticResponseFeedback;
